import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('[GearControl] Missing Supabase environment variables. Check your .env file.');
}

// Clear stale/invalid auth tokens from storage to prevent refresh token errors
const clearStaleAuthTokens = () => {
  try {
    const storageKey = 'sb-gearcontrol-auth-token';
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      // If token exists but has no refresh_token or is clearly malformed, remove it
      if (!parsed?.refresh_token) {
        console.warn('[GearControl] Removing stale auth token from storage');
        localStorage.removeItem(storageKey);
      }
    }
  } catch (e) {
    // Ignore parse errors — just clear the key
    try { localStorage.removeItem('sb-gearcontrol-auth-token'); } catch (_) {}
  }
};

const forceSignOutAndClear = () => {
  try {
    localStorage.removeItem('sb-gearcontrol-auth-token');
    // Also clear any other supabase-related keys
    Object.keys(localStorage)?.forEach(key => {
      if (key?.startsWith('sb-') && key?.includes('auth')) {
        localStorage.removeItem(key);
      }
    });
  } catch (_) {}
};

let supabaseInstance = null;

const getSupabaseClient = () => {
  if (supabaseInstance) return supabaseInstance;

  // Guard: if env vars are missing, return a safe no-op proxy to avoid crashing
  if (!supabaseUrl || !supabaseAnonKey) {
    console.error('[GearControl] Supabase client cannot be initialized — missing env vars.');
    return {
      from: () => ({ select: () => Promise.resolve({ data: null, error: new Error('Supabase not configured') }) }),
      auth: {
        getSession: () => Promise.resolve({ data: { session: null }, error: null }),
        refreshSession: () => Promise.resolve({ data: { session: null }, error: null }),
        onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
        signInWithPassword: () => Promise.resolve({ data: null, error: new Error('Supabase not configured') }),
        signUp: () => Promise.resolve({ data: null, error: new Error('Supabase not configured') }),
        signOut: () => Promise.resolve({ error: null }),
        resetPasswordForEmail: () => Promise.resolve({ data: null, error: new Error('Supabase not configured') }),
        updateUser: () => Promise.resolve({ data: null, error: new Error('Supabase not configured') }),
      },
      storage: { from: () => ({}) },
      channel: () => ({ on: () => ({ subscribe: () => {} }) }),
      removeChannel: () => {},
    };
  }

  supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storageKey: 'sb-gearcontrol-auth-token',
      lock: async (name, acquireTimeout, fn) => {
        return fn();
      }
    }
  });

  // Listen for auth errors and clear invalid refresh tokens automatically
  supabaseInstance?.auth?.onAuthStateChange((event, session) => {
    if (event === 'TOKEN_REFRESHED' && !session) {
      console.warn('[GearControl] Token refresh failed — clearing stored session');
      forceSignOutAndClear();
    }
    if (event === 'SIGNED_OUT') {
      // Ensure storage is fully cleared on sign out to prevent stale token reuse
      forceSignOutAndClear();
    }
  });

  return supabaseInstance;
};

// Run cleanup on module load to remove any stale tokens before client init
clearStaleAuthTokens();

export const supabase = getSupabaseClient();
